import { map, clamp } from '../../js/utils/math.js';
import Swiper from 'swiper';
import EffectCards from 'swiper/modules/effect-cards.mjs';
import 'swiper/swiper.min.css';

export default class Gallery {
  init(root) {
    this.root = root;
    this.gallery = this.root;
    this.galleryWrapper = this.root.querySelector(".gallery-wrapper");
    this.items = Array.from(this.galleryWrapper.children);
    this.totalItems = this.items.length;
    this.hoveredIndex;

    if (window.innerWidth < 820) {
      this.mobileVersion = true;
    } else {
      this.mobileVersion = false;
    }

    this.waitForImages().then(() => {
      this.root.style.opacity = 1; // Show root when all images are loaded

      if (this.mobileVersion != true) {
        this.setupGallery();
      }

      if (this.mobileVersion === true) {
        this.setupSwiper();
      }
    });

    // Resize event listener to switch between swiper and gallery
    window.addEventListener("resize", this.onResize.bind(this));
  }

  onResize() {
    const isMobile = window.innerWidth < 820;

    if (isMobile && !this.mobileVersion) {
      this.mobileVersion = true;
      this.destroyGallery();
      this.setupSwiper();
    } else if (!isMobile && this.mobileVersion) {
      this.mobileVersion = false;
      this.destroySwiper();
      this.setupGallery();
    }
  }

  setupGallery() {
    // console.log("setupGallery");
    this.setItemStyles();
    this.root.style.opacity = 1;

    // Create references for the event listeners
    this.onHoverHandler = (event) => this.onHover(event.currentTarget);
    this.onLeaveHandler = () => this.onLeave();

    this.items.forEach((item) => {
      item.addEventListener("mouseenter", this.onHoverHandler);
      item.addEventListener("mouseleave", this.onLeaveHandler);
    });

    window.addEventListener("resize", this.setItemStyles.bind(this));
  }

  destroyGallery() {
    // console.log("destroyGallery");
    this.items.forEach((item) => {
      item.removeEventListener("mouseenter", this.onHoverHandler);
      item.removeEventListener("mouseleave", this.onLeaveHandler);
    });

    window.removeEventListener("resize", this.setItemStyles.bind(this));
  }

  setupSwiper() {
    // console.log("setupSwiper");
    this.gallery.classList.add("swiper");
    this.gallery.classList.remove("gallery");
    // this.galleryWrapper.classList.remove("gallery-wrapper");
    // Ensure gallery element is valid before initializing swiper
    // console.log('EffectCards')
    // console.log(EffectCards)
    if (this.gallery && this.items.length > 0 && !this.swiperInstance) {
      this.swiperInstance = new Swiper(this.root, {
        effect: "cards",
        modules: [EffectCards],
        grabCursor: true,
      });
    }
  }

  destroySwiper() {
    // console.log("destroySwiper");
    this.root.classList.add("gallery");
    this.root.classList.remove("swiper");
    // this.galleryWrapper.classList.add("gallery-wrapper");
    if (this.swiperInstance) {
      this.swiperInstance.destroy(true, true); // Properly destroy the swiper instance
      this.swiperInstance = null;
    }
  }

  setItemStyles() {
    // console.log("setItemStyles");
    this.galleryWidth = this.root.offsetWidth;

    let scale = clamp(map(this.galleryWidth, 0, 1200, 2.5, 1.4), 1.4, 2.5);

    // this.galleryWrapper.style.margin = `${scale * 10 - 10}% auto`;
    this.galleryWrapper.style.setProperty("--spaceoverflow", `${scale.toFixed(3) * 10 - 10}%`);

    this.items.forEach((item, index) => {
      const tilt = (Math.random() - 0.5) * 20;
      const arcOffset =
        Math.floor(Math.sin((index / (this.totalItems - 1)) * Math.PI) * -20) +
        10;

      item.style.setProperty("--tilt", `${tilt}deg`);
      
      item.style.setProperty("--scale",`${scale}`);
      item.style.setProperty("--index", index);
      item.style.setProperty("--arc-offset", `${arcOffset}%`);
      item.style.setProperty("--hoffset", `${0}%`);
    });
  }

  onHover(hoveredItem) {
    // console.log("onHover");
    const newHoveredIndex = Number(
      hoveredItem.style.getPropertyValue("--index")
    );

    if (newHoveredIndex < this.hoveredIndex) {
      //the new element is to the left
      hoveredItem.style.zIndex = 1;
    } else {
      // the new element is to the right
      //it's likely overlapping the las hovered element. Animation is likely smoother if we delay the z-index update
      setTimeout(() => {
        if (hoveredItem.classList.contains("active")) {
          hoveredItem.style.zIndex = 1;
        }
      }, 200);
    }

    this.hoveredIndex = newHoveredIndex;
    const totalItems = this.items.length;

    hoveredItem.classList.add("active");

    const maxMovement = 50; // Max movement near hovered item

    this.items.forEach((item) => {
      const index = Number(item.style.getPropertyValue("--index"));
      const movementLeftNeigbour = map(
        index,
        0,
        this.hoveredIndex,
        0,
        maxMovement
      );
      const movementRightNeigbour = map(
        index,
        this.hoveredIndex,
        totalItems - 1,
        maxMovement,
        0
      );

      let moveDistance = 0;
      if (index < this.hoveredIndex) {
        moveDistance = movementLeftNeigbour;
      } else if (index > this.hoveredIndex) {
        moveDistance = movementRightNeigbour;
      }

      const moveDirection = index < this.hoveredIndex ? -1 : 1;
      item.style.setProperty("--hoffset", `${moveDirection * moveDistance}%`);
    });

    hoveredItem.style.setProperty("--hoffset", `${0}%`);
  }

  onLeave() {
    // console.log("onLeave");
    this.items.forEach((item) => {
      item.classList.remove("active");
      item.style.setProperty("--hoffset", `${0}%`);
      item.style.zIndex = 0;
    });
  }

  waitForImages() {
    // console.log("waitForImages");
    return new Promise((resolve) => {
      const images = Array.from(this.gallery.querySelectorAll("img"));
      let loadedCount = 0;

      if (images.length === 0) {
        resolve(); // No images, resolve immediately
        return;
      }

      images.forEach((img) => {
        if (img.complete) {
          loadedCount++;
        } else {
          img.addEventListener("load", () => {
            loadedCount++;
            if (loadedCount === images.length) {
              resolve();
            }
          });
          img.addEventListener("error", () => {
            loadedCount++; // Even if an image fails, count it
            if (loadedCount === images.length) {
              resolve();
            }
          });
        }
      });

      // If all images were already loaded (cached), resolve immediately
      if (loadedCount === images.length) {
        resolve();
      }
    });
  }
}
